import { useState, useEffect, useRef, useCallback } from 'react';
import { RoomEvent, RoomMemberEvent } from '../constants/matrixEvents';
import logger from '../utils/logger';
import { SUPPORTED_MESSAGE_TYPES } from '../constants';

/**
 * Maximum number of events to keep in the window
 * This helps manage memory usage
 */
const MAX_WINDOW_SIZE = 500;

/**
 * Number of events to paginate when loading more
 */
const PAGINATION_SIZE = 50;

/**
 * Minimum number of events to keep when unpaginating
 * to avoid aggressive pruning
 */
const MIN_WINDOW_SIZE = 100;

/**
 * Target number of events to ensure viewport is filled
 * The hook will recursively paginate until this threshold is met
 */
const VIEWPORT_FILL_TARGET = 30;

/**
 * Maximum number of recursive pagination attempts
 * Prevents infinite loops in edge cases
 */
const MAX_FILL_ATTEMPTS = 10;

const ATTACHMENT_MESSAGE_TYPES = new Set(['m.file', 'm.image', 'm.video', 'm.audio']);
const FAILED_LOCAL_ECHO_STATUSES = new Set(['not_sent', 'cancelled']);

const isPendingTimelineEvent = (event) => (
  event?.status !== null && event?.status !== undefined && event?.status !== 'cancelled'
);

const getTimelineEventContent = (event) => (
  event?.getClearContent?.() || event?.getContent?.() || null
);

const shouldAnchorFailedAttachmentAtTimestamp = (event) => {
  const content = getTimelineEventContent(event);
  return ATTACHMENT_MESSAGE_TYPES.has(content?.msgtype) && FAILED_LOCAL_ECHO_STATUSES.has(event?.status);
};

/**
 * Paginate a timeline with sequential decryption to prevent WASM crypto panics.
 *
 * The SDK's event mapper fires concurrent decryptEventIfNeeded calls for all
 * paginated events. With 50+ encrypted messages, this causes concurrent access
 * to the WASM crypto module's RefCell-based tracing subscriber, triggering
 * "panic_already_mutably_borrowed" panics. After failure, events are permanently
 * marked as m.bad.encrypted (setClearDataForDecryptionFailure) and CANNOT be
 * retried via decryptEventIfNeeded.
 *
 * This helper:
 * 1. Temporarily disables auto-decrypt in the event mapper
 * 2. Performs the pagination
 * 3. Restores the event mapper
 * 4. Decrypts events sequentially (one at a time)
 *
 * @returns {Promise<boolean>} Pagination result from SDK
 */
const paginateWithSequentialDecrypt = async (client, timeline, options, cancelledRef, isDecryptingRef) => {
  // Suppress auto-decrypt during pagination
  const originalGetEventMapper = client.getEventMapper.bind(client);
  client.getEventMapper = (opts) => originalGetEventMapper({ ...opts, decrypt: false });

  let result;
  try {
    result = await client.paginateEventTimeline(timeline, options);
  } finally {
    // Restore immediately — even if pagination throws
    client.getEventMapper = originalGetEventMapper;
  }

  if (!result) return false;

  // Events have clean state: never attempted decrypt, so:
  //   getType() returns "m.room.encrypted" (original event type)
  //   getClearContent() returns null
  //   shouldAttemptDecryption() returns true
  const events = timeline.getEvents();
  const encrypted = events.filter(
    e => e.getType() === 'm.room.encrypted' && !e.getClearContent(),
  );

  if (encrypted.length > 0) {
    if (isDecryptingRef) isDecryptingRef.current = true;
    try {
      logger.debug(`[TimelineWindow] Sequentially decrypting ${encrypted.length} events`);
      let decryptedCount = 0;
      for (const event of encrypted) {
        if (cancelledRef?.current) break;
        try {
          await client.decryptEventIfNeeded(event);
          if (event.getClearContent()) {
            decryptedCount++;
          }
        } catch {
          // Silent — will show "waiting for keys" or error in UI
        }
      }
      if (decryptedCount > 0) {
        logger.debug(`[TimelineWindow] Decrypted ${decryptedCount}/${encrypted.length} events`);
      }
    } finally {
      if (isDecryptingRef) isDecryptingRef.current = false;
    }
  }

  return true;
};

/**
 * Hook for managing a timeline window with pagination and memory management
 *
 * @param {Object} room - The Matrix room object
 * @param {string} roomId - The room ID for reference
 * @returns {Object} Timeline window state and controls
 */
export const useTimelineWindow = (room, roomId) => {
  const [events, setEvents] = useState([]);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isLoadingNewer, setIsLoadingNewer] = useState(false);
  const [canPaginateBackward, setCanPaginateBackward] = useState(true);
  const [canPaginateForward, setCanPaginateForward] = useState(false);
  const [windowPosition, setWindowPosition] = useState('live'); // 'live', 'historical', or 'middle'
  const [isFillingViewport, setIsFillingViewport] = useState(false);

  // Keep track of the timeline we're working with
  const timelineRef = useRef(null);
  const eventTimelineSetRef = useRef(null);
  const fillViewportTimeoutRef = useRef(null);
  const refreshingRef = useRef(false);
  const decryptCancelledRef = useRef(false);
  const isDecryptingRef = useRef(false);

  /**
   * Get events from timeline, keeping pending local echoes after confirmed events.
   * addEventsToTimeline can corrupt the SDK's internal event order by prepending
   * events out of chronological order. Always sorting eliminates the entire class
   * of flag-management bugs where downstream reads (handleTimelineUpdate,
   * fillViewport, initializeTimeline) could see unsorted data.
   *
  * Pending events keep their original local timestamp across manual retries.
  * If we sort only by getTs(), a retried local echo can jump ahead of a newer
  * confirmed remote message. Element keeps retryable pending messages at the
  * bottom until they are confirmed, so we mirror that behavior here.
  *
  * Failed attachment local echoes are the exception: they are not retried by
  * the app, so they must stay anchored to the timestamp of the original send
  * attempt instead of jumping to the end whenever newer confirmed events land.
  *
  * Complexity stays O(n log n) for the sort itself (plus O(n) to clone the
  * timeline array). The extra pending-status check only changes the constant
  * factor versus the previous timestamp-only comparator.
   */
  const getSortedEvents = useCallback((timeline) => {
    const events = [...timeline.getEvents()];
    events.sort((a, b) => {
      const aPending = isPendingTimelineEvent(a) && !shouldAnchorFailedAttachmentAtTimestamp(a);
      const bPending = isPendingTimelineEvent(b) && !shouldAnchorFailedAttachmentAtTimestamp(b);

      if (aPending !== bPending) {
        return aPending ? 1 : -1;
      }

      return a.getTs() - b.getTs();
    });
    return events;
  }, []);

  /**
   * Check if we can paginate backward based on Matrix SDK pagination tokens
   * This is the standard and recommended approach with a fallback
   */
  const canPaginateBackwardFromToken = useCallback((timeline) => {
    if (!timeline) {
      return false;
    }
    const backwardToken = timeline.getPaginationToken('b');
    const hasToken = backwardToken !== null && backwardToken !== undefined;

    if (!hasToken) {
      const events = getSortedEvents(timeline);
      if (events.length > 0) {
        const oldestEvent = events[0];
        const isRoomCreate = oldestEvent.getType() === 'm.room.create';
        return !isRoomCreate;
      }
    }
    return hasToken;
  }, [getSortedEvents]);

  /**
   * Recursively fill the viewport by paginating backward until we have enough content
   */
  // Helper: count visible (displayable) messages in a list of events
  const countVisibleMessages = (events) => {
    let count = 0;
    for (const event of events) {
      // Room creation event is always visible
      if (event.getType() === 'm.room.create') {
        count++;
        continue;
      }
      // Decrypted message
      if (event.getClearContent && event.getClearContent()) {
        const clearContent = event.getClearContent();
        if (SUPPORTED_MESSAGE_TYPES.includes(clearContent.msgtype)) {
          count++;
        }
        continue;
      }
      // Unencrypted message
      if (event.getType() === 'm.room.message') {
        const content = event.getContent();
        if (SUPPORTED_MESSAGE_TYPES.includes(content.msgtype)) {
          count++;
        }
        continue;
      }
      // Show encrypted messages that failed to decrypt
      if (event.getType() === 'm.room.encrypted') {
        count++;
        continue;
      }
    }
    return count;
  };

  const fillViewport = useCallback(
    async (currentAttempts = 0) => {
      // Clear any pending timeout to prevent orphaned timers when fillViewport is called again
      if (fillViewportTimeoutRef.current) {
        clearTimeout(fillViewportTimeoutRef.current);
        fillViewportTimeoutRef.current = null;
      }

      if (!room || !timelineRef.current || currentAttempts >= MAX_FILL_ATTEMPTS || isDecryptingRef.current) {
        logger.debug(`[TimelineWindow] fillViewport: Stopping (room? ${!!room}, timeline? ${!!timelineRef.current}, attempts=${currentAttempts})`);
        setIsFillingViewport(false);
        return;
      }

      const timeline = timelineRef.current;
      const currentEvents = timeline.getEvents();
      const visibleCount = countVisibleMessages(currentEvents);

      // Check if we have enough visible messages or can't paginate anymore
      if (visibleCount >= VIEWPORT_FILL_TARGET || !canPaginateBackward) {
        setIsFillingViewport(false);
        return;
      }

      try {
        setIsFillingViewport(true);

        // Track previous event count for infinite loop detection
        const prevCount = timeline.getEvents().length;

        // Paginate backward to get more content (sequential decrypt to avoid WASM panics)
        const paginateResult = await paginateWithSequentialDecrypt(
          room.client, timeline,
          { backwards: true, limit: PAGINATION_SIZE },
          decryptCancelledRef,
          isDecryptingRef,
        );

        const updatedEvents = timeline.getEvents();
        const newCount = updatedEvents.length;
        const newVisibleCount = countVisibleMessages(updatedEvents);

        if (paginateResult) {
          if (newCount === prevCount) {
            logger.warn('[TimelineWindow] fillViewport: Paginate returned no new events, breaking to avoid infinite loop.');
            setIsFillingViewport(false);
            return;
          }
          // ...existing code...
          setEvents(getSortedEvents(timeline));

          // Check if we can still paginate backward using Matrix SDK tokens
          const canStillPaginate = canPaginateBackwardFromToken(timeline);

          setCanPaginateBackward(canStillPaginate);

          // Recursively continue filling if we still need more visible messages
          if (newVisibleCount < VIEWPORT_FILL_TARGET && canStillPaginate) {
            // Use a small delay to prevent overwhelming the server
            fillViewportTimeoutRef.current = setTimeout(() => {
              fillViewport(currentAttempts + 1);
            }, 100);
          } else {
            setIsFillingViewport(false);
          }
        } else {
          setCanPaginateBackward(false); // No more events available
          setIsFillingViewport(false);
        }
      } catch (error) {
        // Retry on M_FORBIDDEN - join may still be in progress (defense in depth)
        if ((error.errcode === 'M_FORBIDDEN' || error.httpStatus === 403) && currentAttempts < 3) {
          logger.warn(`[TimelineWindow] fillViewport: M_FORBIDDEN for room ${roomId} - retrying after membership settles (attempt ${currentAttempts + 1}/3)`);
          fillViewportTimeoutRef.current = setTimeout(() => fillViewport(currentAttempts + 1), 500);
          return;
        }
        logger.error('useTimelineWindow: Error during viewport fill:', error);
        setIsFillingViewport(false);
      }
    },
  [room, roomId, canPaginateBackward, canPaginateBackwardFromToken, getSortedEvents],
  );

  /**
   * Initialize the timeline window
   * @param {boolean} skipMembershipCheck - Skip membership check when called from membership listener
   *   (the room object's getMyMembership() may not be updated yet when the event fires)
   */
  const initializeTimeline = useCallback((skipMembershipCheck = false) => {
    if (!room) {
      setEvents([]);
      setCanPaginateBackward(true);
      setCanPaginateForward(false);
      setWindowPosition('live');
      setIsFillingViewport(false);
      return;
    }

    // Check membership before initializing timeline
    // Prevents M_FORBIDDEN errors when auto-accepting invites
    // Skip this check when called from membership listener (membership event has confirmed join)
    if (!skipMembershipCheck) {
      const membership = room.getMyMembership();
      if (membership !== 'join') {
        logger.debug(`[TimelineWindow] Skipping init for room ${roomId} - membership is "${membership}"`);
        setEvents([]);
        setCanPaginateBackward(false);
        setCanPaginateForward(false);
        setWindowPosition('live');
        setIsFillingViewport(false);
        return;
      }
    }

    // Get the live timeline
    const liveTimeline = room.getLiveTimeline();
    const eventTimelineSet = room.getTimelineSets()[0]; // Get the main timeline set

    timelineRef.current = liveTimeline;
    eventTimelineSetRef.current = eventTimelineSet;

    // Check if we can paginate using Matrix SDK pagination tokens
    const canInitiallyPaginateBackward = canPaginateBackwardFromToken(liveTimeline);

    setCanPaginateBackward(canInitiallyPaginateBackward);
    setCanPaginateForward(false); // Can't paginate forward from live
    setWindowPosition('live');

    // Skip setEvents if refreshTimeline is in progress — it will set events after adding missed messages
    if (!refreshingRef.current) {
      const initialEvents = getSortedEvents(liveTimeline);
      setEvents(initialEvents);

      // Start filling the viewport if we don't have enough content
      if (initialEvents.length < VIEWPORT_FILL_TARGET) {
        fillViewport(0);
      }
    }
  }, [room, roomId, fillViewport, canPaginateBackwardFromToken, getSortedEvents]);

  /**
   * Paginate backward (load older events)
   */
  const paginateBackward = useCallback(async () => {
    // Check membership to prevent M_FORBIDDEN errors during invite auto-accept
    if (!room || room.getMyMembership() !== 'join' || !timelineRef.current || isLoadingOlder || !canPaginateBackward || isDecryptingRef.current) {
      return;
    }

    setIsLoadingOlder(true);

    try {
      // Use sequential decrypt to load older events (prevents WASM crypto panics)
      const paginateResult = await paginateWithSequentialDecrypt(
        room.client, timelineRef.current,
        { backwards: true, limit: PAGINATION_SIZE },
        decryptCancelledRef,
        isDecryptingRef,
      );

      if (paginateResult) {
        // Get the updated events from the timeline
        const updatedEvents = getSortedEvents(timelineRef.current);
        setEvents(updatedEvents);
        logger.debug(
          `[TimelineWindow] paginateBackward: Paginated backward, now have ${updatedEvents.length} events.`,
        );
        // Check if we can still paginate backward using Matrix SDK tokens
        const canStillPaginate = canPaginateBackwardFromToken(timelineRef.current);
        setCanPaginateBackward(canStillPaginate);

        // If we were at live position and loaded older events, we're now in historical view
        if (windowPosition === 'live') {
          setWindowPosition('historical');
          setCanPaginateForward(true); // Now we can paginate forward to get back to live
        }

        // Manage memory by unpaginating if we have too many events
        await manageWindowSize();
      } else {
        setCanPaginateBackward(false); // No more events available
      }
    } catch (error) {
      logger.error('useTimelineWindow: Error paginating backward:', error);
    } finally {
      setIsLoadingOlder(false);
    }
  }, [
    room,
    roomId,
    isLoadingOlder,
    canPaginateBackward,
    windowPosition,
    canPaginateBackwardFromToken,
    getSortedEvents,
  ]);

  /**
   * Paginate forward (load newer events)
   */
  const paginateForward = useCallback(async () => {
    // Check membership to prevent M_FORBIDDEN errors during invite auto-accept
    if (!room || room.getMyMembership() !== 'join' || !timelineRef.current || isLoadingNewer || !canPaginateForward || isDecryptingRef.current) {
      return;
    }

    setIsLoadingNewer(true);

    try {
      // Use sequential decrypt to load newer events (prevents WASM crypto panics)
      const paginateResult = await paginateWithSequentialDecrypt(
        room.client, timelineRef.current,
        { backwards: false, limit: PAGINATION_SIZE },
        decryptCancelledRef,
        isDecryptingRef,
      );

      if (paginateResult) {
        // Get the updated events from the timeline
        const updatedEvents = getSortedEvents(timelineRef.current);
        setEvents(updatedEvents);

        // Check if we can still paginate forward
        const canStillPaginate = timelineRef.current.getPaginationToken('f') !== null;
        setCanPaginateForward(canStillPaginate);

        // If we can't paginate forward anymore, we're back at live
        if (!canStillPaginate) {
          setWindowPosition('live');
        } else {
          setWindowPosition('middle');
        }

        // Manage memory by unpaginating if we have too many events
        await manageWindowSize();
      }
    } catch (error) {
      logger.error('useTimelineWindow: Error paginating forward:', error);
    } finally {
      setIsLoadingNewer(false);
    }
  }, [room, roomId, isLoadingNewer, canPaginateForward, getSortedEvents]);

  /**
   * Jump to live timeline (most recent events)
   */
  const jumpToLive = useCallback(() => {
    if (!room) return;

    // Get the live timeline and reset our window
    const liveTimeline = room.getLiveTimeline();
    timelineRef.current = liveTimeline;

    setEvents(getSortedEvents(liveTimeline));
    setWindowPosition('live');
    setCanPaginateBackward(true);
    setCanPaginateForward(false);
  }, [room, getSortedEvents]);

  // Guard ref for concurrent jumpToEvent calls
  const isJumpingRef = useRef(false);

  /**
   * Jump to a specific event in the timeline by paginating backward from the
   * live timeline to locate it. Does NOT require timelineSupport.
   *
   * If found, updates the event list and keeps windowPosition='live' so that
   * incoming /sync events continue to be processed by handleTimelineUpdate.
   * If not found, returns false. Note: backward pagination performed during
   * the search is not reverted on the underlying timeline object.
   *
   * @param {string} eventId - The event ID to jump to
   * @returns {Promise<boolean>} True if jump succeeded (event is now in the timeline)
   */
  const jumpToEvent = useCallback(async (eventId) => {
    if (!room || !room.client || room.getMyMembership() !== 'join') return false;
    if (isDecryptingRef.current || isJumpingRef.current) return false;

    isJumpingRef.current = true;

    try {
      const timeline = room.getLiveTimeline();

      // Check if event is already in the current timeline view
      if (timeline.getEvents().find(e => e.getId() === eventId)) {
        logger.debug(`[TimelineWindow] jumpToEvent: event ${eventId} already in timeline`);
        return true;
      }

      // Paginate backward to find the target event
      const maxAttempts = 10;
      let found = false;

      for (let i = 0; i < maxAttempts; i++) {
        if (decryptCancelledRef.current) break;
        if (!timeline.getPaginationToken('b')) break;

        await paginateWithSequentialDecrypt(
          room.client, timeline,
          { backwards: true, limit: PAGINATION_SIZE },
          decryptCancelledRef, isDecryptingRef,
        );

        if (timeline.getEvents().find(e => e.getId() === eventId)) {
          found = true;
          break;
        }
      }

      if (!found) {
        logger.debug(`[TimelineWindow] jumpToEvent: event ${eventId} not found after ${maxAttempts} attempts`);
        return false;
      }

      // Update the displayed events — keep windowPosition='live' so that
      // handleTimelineUpdate continues processing incoming /sync events
      const finalEvents = getSortedEvents(timeline);
      logger.debug(`[TimelineWindow] jumpToEvent: found, ${finalEvents.length} events loaded`);

      setEvents(finalEvents);
      timelineRef.current = timeline;
      setCanPaginateBackward(canPaginateBackwardFromToken(timeline));
      // Enable forward pagination since we paginated backward from live
      setCanPaginateForward(true);
      return true;
    } catch (error) {
      logger.error('[TimelineWindow] jumpToEvent failed:', error);
      return false;
    } finally {
      isJumpingRef.current = false;
    }
  }, [room, getSortedEvents, canPaginateBackwardFromToken]);

  /**
   * Fetch potentially missed messages by paginating both forward and backward.
   * Used when ignore list changes to fetch messages that were filtered by the server
   * during the ignore period, without resetting the existing timeline.
   *
   * When a user is ignored, the Matrix server filters their messages from /sync responses.
   * This means the client never receives those messages - they exist on the server but
   * are not in the local timeline. When unignoring:
   * - Messages sent DURING the ignore period are "newer" than the client's last sync
   * - Forward pagination from live timeline catches these missed messages
   * - Backward pagination catches any gaps in older messages
   *
   * Note: Messages already in the timeline will automatically reappear when the
   * ignoredUsers filter is updated (via messageGroups useMemo in useRoom).
   * This function handles the case where messages were NEVER received because
   * the server filtered them during sync.
   */
  const refreshTimeline = useCallback(async () => {
    if (!room || !room.client || room.getMyMembership() !== 'join') {
      return;
    }

    // Concurrency guard: prevent overlapping refreshTimeline calls from racing
    if (refreshingRef.current) {
      return;
    }
    refreshingRef.current = true;

    try {
      // Capture pre-refresh timeline reference to detect if reset already happened
      const preRefreshTimeline = timelineRef.current || room.getLiveTimeline();

      // Wait for the timeline reset triggered by the limited sync after ignore list change.
      // When unignoring a user, the server sends a sync with limited:true which causes
      // the SDK to reset the room timeline. We must wait for this reset so we work with
      // the NEW post-reset timeline that includes previously-filtered messages.
      //
      // Account data (m.ignored_user_list) is processed before room timelines in the SDK,
      // so RoomEvent.TimelineReset fires AFTER refreshTimeline is called from the
      // account data handler.
      const timelineAlreadyReset = room.getLiveTimeline() !== preRefreshTimeline;

      if (!timelineAlreadyReset) {
        await new Promise((resolve) => {
          let resolved = false;
          const finish = () => {
            if (resolved) return;
            resolved = true;
            room.client.off(RoomEvent.TimelineReset, onReset);
            clearTimeout(timeoutId);
            resolve();
          };
          const onReset = (resetRoom) => {
            if (resetRoom?.roomId === roomId) {
              finish();
            }
          };
          room.client.on(RoomEvent.TimelineReset, onReset);
          const timeoutId = setTimeout(() => {
            finish();
          }, 5000);
        });
      }

      // Small delay to let initializeTimeline() from reset handler complete its work
      // (it updates timelineRef.current to the new timeline)
      await new Promise(r => setTimeout(r, 100));

      // Re-acquire the live timeline (post-reset)
      const liveTimeline = room.getLiveTimeline();
      timelineRef.current = liveTimeline;

      // When a user is ignored, the server filters their messages from /sync.
      // These filtered messages are NEVER sent to the client, so they're not in the local timeline.
      // Pagination doesn't help because it uses existing timeline tokens.
      //
      // Solution: Use createMessagesRequest to fetch directly from /messages API,
      // which returns ALL messages regardless of ignore status.
      try {
        // Get the most recent events from the server, working backwards from "now"
        // Empty string token means "from the end of the timeline"
        const messagesResponse = await room.client.createMessagesRequest(
          roomId,
          '',  // empty token = start from the end
          PAGINATION_SIZE,
          'b', // backward direction
        );

        if (messagesResponse?.chunk?.length > 0) {
          // Map raw events to MatrixEvents
          const mapper = room.client.getEventMapper();
          const matrixEvents = messagesResponse.chunk.map(mapper);

          // Check which events are new (not already in timeline)
          const existingIds = new Set(liveTimeline.getEvents().map(e => e.getId()));
          const newEvents = matrixEvents.filter(e => !existingIds.has(e.getId()));

          if (newEvents.length > 0) {
            logger.info(`[TimelineWindow] Found ${newEvents.length} missed events via /messages API`);
            // Add new events to the timeline
            room.addEventsToTimeline(newEvents, true, true, liveTimeline);
          }
        }
      } catch (fetchError) {
        logger.warn('[TimelineWindow] /messages API fetch failed:', fetchError.message);
      }

      // Also try regular backward pagination to fill any gaps (sequential decrypt)
      const canPaginateBwd = canPaginateBackwardFromToken(liveTimeline);
      if (canPaginateBwd) {
        await paginateWithSequentialDecrypt(
          room.client, liveTimeline,
          { backwards: true, limit: PAGINATION_SIZE },
          decryptCancelledRef,
          isDecryptingRef,
        );
      }

      // Sort all events chronologically by origin_server_ts.
      // This fixes ordering issues from addEventsToTimeline prepending events
      // that belong in the middle of the timeline.
      const updatedEvents = getSortedEvents(liveTimeline);

      setEvents(updatedEvents);
      setWindowPosition('live');
      setCanPaginateBackward(canPaginateBackwardFromToken(liveTimeline));
      setCanPaginateForward(false);

      // Fill viewport if we don't have enough content after refresh
      if (countVisibleMessages(updatedEvents) < VIEWPORT_FILL_TARGET) {
        fillViewport(0);
      }

      // Attempt to decrypt any encrypted events that don't have clear content
      // This helps recover messages that were sent while the user was ignored
      // and whose keys may now be available
      const undecryptableEvents = updatedEvents.filter(e =>
        e.getType() === 'm.room.encrypted' && !e.getClearContent(),
      );

      if (undecryptableEvents.length > 0 && room.client?.decryptEventIfNeeded) {
        isDecryptingRef.current = true;
        try {
          let decryptedCount = 0;
          for (const event of undecryptableEvents) {
            if (decryptCancelledRef.current) break;
            try {
              await room.client.decryptEventIfNeeded(event);
              if (event.getClearContent()) {
                decryptedCount++;
              }
            } catch {
              // Silently continue - some messages may not have keys available
            }
          }

          // If we decrypted any events, update the events state to trigger re-render
          if (decryptedCount > 0) {
            logger.debug(`[TimelineWindow] Decrypted ${decryptedCount} events after refresh`);
            setEvents([...updatedEvents]);
          }
        } finally {
          isDecryptingRef.current = false;
        }
      }

    } catch (error) {
      logger.error('[TimelineWindow] Failed to refresh timeline:', error);
      // Fallback: show current timeline state even on error
      if (room) {
        setEvents(getSortedEvents(room.getLiveTimeline()));
      }
    } finally {
      refreshingRef.current = false;
      // Signal completion to other hooks (e.g., useRoomList preview update)
      window.dispatchEvent(new CustomEvent('matrix:timeline:refreshed', {
        detail: { roomId },
      }));
    }
  }, [room, roomId, canPaginateBackwardFromToken, fillViewport, getSortedEvents]);

  /**
   * Manage window size by unpaginating old events to prevent memory issues
   */
  const manageWindowSize = useCallback(async () => {
    if (!timelineRef.current || events.length <= MAX_WINDOW_SIZE) {
      return;
    }

    try {
      // If we're viewing historical content and have too many events,
      // unpaginate from the end (newer events) to keep the older ones visible
      if (windowPosition === 'historical') {
        // Keep the older events, remove newer ones
        const eventsToKeep = MIN_WINDOW_SIZE;
        const timeline = timelineRef.current;
        const allEvents = getSortedEvents(timeline);

        if (allEvents.length > eventsToKeep) {
          // This is a simplified approach - in a real implementation,
          // you might want to use the Matrix SDK's unpaginate methods
          // For now, we'll just work with a subset
          const keptEvents = allEvents.slice(0, eventsToKeep);
          setEvents(keptEvents);
          setCanPaginateForward(true); // We removed newer events, so we can paginate forward
        }
      } else {
        // If we're viewing live or middle content, keep the newer events
        const eventsToKeep = MIN_WINDOW_SIZE;
        const timeline = timelineRef.current;
        const allEvents = getSortedEvents(timeline);

        if (allEvents.length > eventsToKeep) {
          const keptEvents = allEvents.slice(-eventsToKeep);
          setEvents(keptEvents);
          setCanPaginateBackward(true); // We removed older events, so we can paginate backward
        }
      }
    } catch (error) {
      logger.error('useTimelineWindow: Error managing window size:', error);
    }
  }, [events.length, windowPosition, getSortedEvents]);

  /**
   * Handle live timeline updates
   */
  const handleTimelineUpdate = useCallback(
    (event, eventRoom, toStartOfTimeline, removed, data) => {
      if (eventRoom?.roomId !== roomId) return;

      // Only process live events when we're viewing the live timeline
      if (data?.liveEvent && windowPosition === 'live') {
        const sortedEvents = getSortedEvents(eventRoom.getLiveTimeline());
        setEvents(sortedEvents);

        // Manage memory for live updates too
        if (sortedEvents.length > MAX_WINDOW_SIZE) {
          manageWindowSize();
        }
      }
    },
  [roomId, windowPosition, manageWindowSize, getSortedEvents],
  );

  /**
   * Handle timeline reset
   */
  const handleTimelineReset = useCallback(
    (eventRoom) => {
      if (eventRoom?.roomId !== roomId) return;
      if (isJumpingRef.current) return; // Don't reset during jumpToEvent
      initializeTimeline();
    },
  [roomId, initializeTimeline],
  );

  // Initialize timeline when room changes
  useEffect(() => {
    initializeTimeline();
  }, [initializeTimeline]);

  // Set up timeline event listeners
  useEffect(() => {
    if (!room?.client) return;

    const client = room.client;

    client.on(RoomEvent.Timeline, handleTimelineUpdate);
    client.on(RoomEvent.TimelineReset, handleTimelineReset);

    return () => {
      client.removeListener(RoomEvent.Timeline, handleTimelineUpdate);
      client.removeListener(RoomEvent.TimelineReset, handleTimelineReset);
    };
  }, [room, handleTimelineUpdate, handleTimelineReset]);

  // Listen for membership changes to re-initialize when user joins
  // This handles the case where timeline init was skipped because membership was 'invite'
  useEffect(() => {
    if (!room?.client) return;

    const handleMembership = (event, member) => {
      if (member.roomId === roomId &&
          member.userId === room.client.getUserId() &&
          member.membership === 'join') {
        logger.debug(`[TimelineWindow] Membership changed to join for room ${roomId}, initializing timeline`);
        // Pass true to skip membership check - the event confirms membership is 'join'
        // (room.getMyMembership() may still return 'invite' at this moment)
        initializeTimeline(true);
      }
    };

    room.client.on(RoomMemberEvent.Membership, handleMembership);
    return () => room.client.off(RoomMemberEvent.Membership, handleMembership);
  }, [room, roomId, initializeTimeline]);

  // Cleanup timeouts and refs on unmount or room change
  useEffect(() => {
    decryptCancelledRef.current = false; // Reset on new room
    isDecryptingRef.current = false; // Reset on new room
    return () => {
      refreshingRef.current = false;
      decryptCancelledRef.current = true; // Cancel any in-flight sequential decrypt
      isDecryptingRef.current = false; // Reset on unmount
      if (fillViewportTimeoutRef.current) {
        clearTimeout(fillViewportTimeoutRef.current);
        fillViewportTimeoutRef.current = null;
      }
    };
  }, [roomId]);

  return {
    events,
    isLoadingOlder,
    isLoadingNewer,
    canPaginateBackward,
    canPaginateForward,
    windowPosition,
    isFillingViewport,
    paginateBackward,
    paginateForward,
    jumpToLive,
    jumpToEvent,
    manageWindowSize,
    initializeTimeline,
    refreshTimeline,
  };
};

export default useTimelineWindow;
